/* eslint-disable jira/import-whitelist */
import { fg } from '@atlassian/jira-feature-gating';
import log from '@atlassian/jira-common-util-logging/src/log.tsx';
import fetchJson from '@atlassian/jira-fetch/src/utils/as-json.tsx';
import { createResourceError } from '@atlassian/jira-router-resources-utils/src/utils/create-resource-error/index.tsx';
import { getSpaStateLastUserLocation } from '@atlassian/jira-spa-state-controller/src/components/main.tsx';
import {
	asyncWrmRequire,
	asyncWrmRequireWithPrefetch,
} from '@atlassian/jira-wrm/src/async-wrm.tsx';
import type { RouterDataContext, ResourceStoreContext } from '@atlassian/react-resource-router';
import { createSessionStorageProvider } from '@atlassian/jira-browser-storage-providers/src/controllers/session-storage/index.tsx';
import { getRequiredResourceList } from '../../utils/index.tsx';

const sessionStorage = createSessionStorageProvider('', false);

const JIRA_HERITAGE_CRITICAL_BATCH = 'jira.heritage.critical';

// PREFETCH ONLY FOR ISSUE VIEW AND PROJECTS-DIRECTORY
// TO-DO: Add it in constant file
const PREFETCH_USER_LOCATIONS = new Set([
	`${'directories'}:${'projects-directory'}`,
	`${'issue'}:${'issue'}`,
]);

// check if the apps loaded on last page contain any app on which prefetching was done.
const isPrefetchRoute = (lastUserLocation: string | undefined) =>
	PREFETCH_USER_LOCATIONS.has(lastUserLocation ?? '');

const buildWebResourcesPayload = (resourceArr: Array<string>) => {
	const payload = {
		// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
		r: [] as string[],
		// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
		c: [] as string[],
		xc: [JIRA_HERITAGE_CRITICAL_BATCH],
		xr: [],
	};

	resourceArr.forEach((resource) => {
		if (resource.indexOf('wrc!') !== -1) {
			payload.c.push(resource.slice(4));
		} else if (resource.indexOf('wr!') !== -1) {
			payload.r.push(resource.slice(3));
		} else {
			payload.r.push(resource);
		}
	});

	return payload;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const generateScriptTags = (response: any) => {
	let resourceHtml = `
    <script type="text/javascript" data-defer-skip>
        window.WRM=window.WRM||{};window.WRM._unparsedData=window.WRM._unparsedData||{};window.WRM._unparsedErrors=window.WRM._unparsedErrors||{};
        WRM._unparsedData = JSON.parse(${JSON.stringify(JSON.stringify(response.unparsedData))});
        WRM._unparsedErrors = JSON.parse(${JSON.stringify(
					JSON.stringify(response.unparsedErrors),
				)});
        if(window.WRM._dataArrived)window.WRM._dataArrived();
    </script>
    `;

	// @ts-expect-error - TS7006 - Parameter 'resource' implicitly has an 'any' type.
	response.resources.forEach((resource) => {
		const { url, resourceType, key } = resource;

		if (resourceType === 'CSS') {
			const cssResource = `<link
                href=${url}
                type="text/css"
                rel="stylesheet"
                media="all"
                data-wrm-key=${key}
                data-wrm-batch-type="context"
            />`;
			resourceHtml += cssResource;
		} else {
			const jsResource = `<div>
                <script
                    type="text/javascript"
                    data-initially-rendered
                    data-defer-skip
                    src=${url}
                    data-wrm-key=${key}
                    data-wrm-batch-type="context"
                ></script>
            </div>`;
			resourceHtml += jsResource;
		}
	});

	return resourceHtml;
};

const getResourceUrl = () => {
	let url = null;
	try {
		if (fg('jfp-vulcan-browser-storage-migration')) {
			url = JSON.parse(sessionStorage.get('classicBatchUrl'));

			// remove the cached value so that it can be used only one time
			sessionStorage.remove('classicBatchUrl');
		} else {
			// @ts-expect-error - TS2345 - Argument of type 'string | null' is not assignable to parameter of type 'string'.
			// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
			url = JSON.parse(window.sessionStorage.getItem('classicBatchUrl'));

			// remove the cached value so that it can be used only one time
			// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
			window.sessionStorage.removeItem('classicBatchUrl');
		}
	} catch {
		return null;
	}

	return url;
};

// eslint-disable-next-line jira/import/no-anonymous-default-export
export default async ({ match, route }: RouterDataContext, _context: ResourceStoreContext) => {
	const boardId = match.query.rapidView ?? match.params.boardId;
	if (!boardId) {
		return null;
	}

	try {
		const contexts = getRequiredResourceList(route ? route.name : null);

		if (
			!__SERVER__ &&
			(route?.name === 'rapidboard-board' ||
				route?.name === 'rapidboard-board-embed' ||
				route?.name === 'servicedesk-boards' ||
				route?.name === 'servicedesk-queues-custom-board' ||
				route?.name === 'servicedesk-queues-practice-custom-board')
		) {
			// get all the apps loaded in last visited page
			const lastUserLocation = getSpaStateLastUserLocation();
			if (isPrefetchRoute(lastUserLocation)) {
				// Get the prefetched batch Url from the session Storage
				const batchUrl = getResourceUrl();
				if (batchUrl) {
					await asyncWrmRequireWithPrefetch(contexts, batchUrl);
					log.safeInfoWithoutCustomerData(
						'spa.view.routes.classic-projects.wrm.prefetch',
						'prefetch success',
					);
					return { contexts };
				}
			}
		}

		if (!__SERVER__) {
			await asyncWrmRequire(contexts);
			return { contexts };
		}

		const payload = buildWebResourcesPayload(contexts);
		const res = await fetchJson('/rest/webResources/1.0/resources?operation=FetchWRMData', {
			method: 'POST',
			body: JSON.stringify(payload),
		});

		return { ...res, contexts, scriptTags: generateScriptTags(res) };
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
	} catch (error: any) {
		throw createResourceError(
			{
				component: 'spa.view.routes.classic-projects.work-all-data.getData',
				message: 'An error was thrown while prefetching work all data',
				name: 'ResourceError',
			},
			error,
		);
	}
};
